import type { Agency, Employee, Shift } from "./types";

// Is the agency operating in tac mode on a specific calendar date?
//   * Master flag off → always false.
//   * Master flag on AND no start date → always true (legacy default).
//   * Master flag on AND start date set → true iff date >= startDate.
// Dates are ISO strings (YYYY-MM-DD) so lexical compare = calendar
// compare without timezone foot-guns.
export function isTacActiveOn(
  agency: Agency | null | undefined,
  date: string,
): boolean {
  if (agency?.tacModeActive !== true) return false;
  if (!agency.tacModeStartDate) return true;
  return date >= agency.tacModeStartDate;
}

// Is this shift "live" on the given calendar date? Three gates:
//   * Tac role vs the tac transition for this date (regular before
//     the start date, tac on/after; exempt always passes).
//   * The shift's coverageInactiveWeekdays list — School Resource
//     dropping coverage on Sat/Sun is the canonical example.
// Used by the schedule grid to grey out days each section doesn't
// own so 0/N short doesn't blare on a day that isn't scheduled.
export function isShiftActiveOn(
  agency: Agency | null | undefined,
  shift: Shift,
  date: string,
): boolean {
  // Weekday gate first — fast and shift-only.
  const wd = new Date(date + "T00:00:00").getDay();
  if ((shift.coverageInactiveWeekdays ?? []).includes(wd)) return false;
  if (shift.tacRole === "exempt") return true;
  const tacOn = isTacActiveOn(agency, date);
  return shift.tacRole === "tac" ? tacOn : !tacOn;
}

// Returns the union of visible shifts across the date window — for
// a page that straddles the tac transition this is both shift
// families (regular + exempt before, tac + exempt after), so the
// schedule grid can render both stripes and the cell-level overlay
// decides which one "owns" each day.
export function visibleShiftsAcross(
  agency: Agency | null | undefined,
  shifts: Shift[],
  dates: string[],
): Shift[] {
  if (dates.length === 0) {
    return applyTacOverlay(agency, shifts, []).visibleShifts;
  }
  const tacOnAny = dates.some((d) => isTacActiveOn(agency, d));
  const regularOnAny = dates.some((d) => !isTacActiveOn(agency, d));
  return shifts.filter((s) => {
    if (s.tacRole === "exempt") return true;
    if (s.tacRole === "tac")     return tacOnAny;
    return regularOnAny;
  });
}

// Per-(officer, date) effective homeShiftId with the tac overlay
// applied. Mirrors applyTacOverlay's projection rule but only on
// dates where tac is active for the agency.
export function effectiveHomeShiftFor(
  agency: Agency | null | undefined,
  emp: Employee,
  date: string,
): string | null {
  // Retirement gate: from the retirement date onward the officer
  // doesn't belong on any shift. Returning null here cascades the
  // right behaviour everywhere — they drop out of the grid
  // grouping, the per-cell sectionOwnsCell check fails, the
  // coverage roster filter skips them.
  if (emp.retirementDate && date >= emp.retirementDate) return null;
  if (emp.followsTacMode && isTacActiveOn(agency, date)) {
    return emp.tacHomeShiftId ?? null;
  }
  return emp.homeShiftId ?? null;
}

// Tac-mode overlay. The agency-wide `tacModeActive` flag flips the
// schedule into "we're so short-staffed we have to work special
// shifts" mode. This helper is a pure projection: given the raw
// shifts / employees and the agency's current tac state, it returns
// what should actually be rendered on the schedule grid.
//
// Non-destructive: the underlying shifts and employees rows in
// Postgres are never altered. Flipping tac mode off restores the
// regular view instantly.
//
// Shift visibility — controlled by each shift's `tacRole`:
//   regular: visible when tac mode is OFF, hidden when ON
//   tac:     visible when tac mode is ON,  hidden when OFF
//   exempt:  always visible (Dispatch, Animal Control, …)
//
// Employee overlay — only when tac mode is ACTIVE:
//   followsTacMode = true  → homeShiftId projects to tacHomeShiftId,
//                            daysOffMode projects to "tac" (so the
//                            roster groups them under their tac
//                            shift and the schedule grid uses the
//                            tac days-off pattern when materialising
//                            XXX cells).
//   followsTacMode = false → unchanged (dispatch civilians, etc.).
export function applyTacOverlay(
  agency: Agency | null | undefined,
  shifts: Shift[],
  employees: Employee[],
): { visibleShifts: Shift[]; effectiveEmployees: Employee[] } {
  const active = agency?.tacModeActive === true;
  const visibleShifts = shifts.filter((s) =>
    active ? s.tacRole !== "regular" : s.tacRole !== "tac");
  if (!active) {
    return { visibleShifts, effectiveEmployees: employees };
  }
  const effectiveEmployees = employees.map((e) =>
    e.followsTacMode
      ? { ...e, homeShiftId: e.tacHomeShiftId, daysOffMode: "tac" as const }
      : e,
  );
  return { visibleShifts, effectiveEmployees };
}
